import SimpleBar from "simplebar-react"
import { useEffect, useRef, useState, useMemo } from "react";
import { Translate } from "../../../components/translate/Translate";
import { useTranslation } from "react-i18next";
import "./styles/ProjectsSelect.scss"
import useNoun from "../../../hooks/useNoun";
import { IEmployer } from "../types";

interface IEmployeesSelect {
	chosenItems: IEmployer[]
	setChosenItems: (list: IEmployer[]) => void
	list: IEmployer[]
	displayAll?: boolean
}

export default function EmployeesSelect({ chosenItems, setChosenItems, list, displayAll } : IEmployeesSelect) {

	// Ensure list is always an array
	const safeList = useMemo(() => Array.isArray(list) ? list : [], [list]);

	const [searchValue, setSearchValue] = useState(""),
	[isActive, setIsActive] = useState(false),
	mainRef = useRef<HTMLDivElement>(null),
	getNoun = useNoun(),
	allCheckboxRef = useRef<HTMLInputElement>(null),
	{ t } = useTranslation();

	const handleSelectCategory = (employer: IEmployer) => {
		if (!chosenItems) return;
		if (chosenItems.some(item => item.id_num === employer.id_num)) {
			setChosenItems(chosenItems.filter(item => item.id_num !== employer.id_num))
		} else {
			const result: IEmployer[] = [];
			safeList.forEach(item => {
				if(item.id_num === employer.id_num) {
					result.push(employer);
				}
				if(chosenItems.some(chosenItem => chosenItem.id_num === item.id_num)) {
					result.push(item);
				}
			})
			
			setChosenItems(result);
		}
	}

	const handleCheckedAll = (checkAll: boolean) => {
		const chosenLength = chosenItems?.length || 0;
		setChosenItems((safeList.length !== chosenLength && checkAll) ? safeList : [])
	}

	useEffect(() => {
		if(mainRef.current) {
			const handleClickOutside = (event: MouseEvent) => {
				if (mainRef.current && !mainRef.current.contains(event.target as Node)) {
					setIsActive(false);
				}
			};
			document.addEventListener('mousedown', handleClickOutside);
			return () => document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [mainRef]);

	useEffect(() => {
		if(displayAll && allCheckboxRef.current && chosenItems && chosenItems.length === 0) {
			setChosenItems(safeList);
			allCheckboxRef.current.checked = true;
		}
		if(chosenItems && chosenItems.length === safeList.length && allCheckboxRef.current) {
			allCheckboxRef.current.checked = true;
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if(!allCheckboxRef.current) return;
		if(!chosenItems || chosenItems.length !== safeList.length) {
			allCheckboxRef.current.checked = false;
		} else {
			allCheckboxRef.current.checked = true;
		}
	}, [chosenItems, safeList]);

	const label = 
		(chosenItems && chosenItems.length) 
	? 
		chosenItems.length === safeList.length 
	? 
		t("analytics_page.statistic_report.all_employees")
	:
		`${getNoun(chosenItems.length, "employees")}`
	:
		t("analytics_page.statistic_report.project_employee");

	return (
		<div className={`projects-select drop-down${isActive ? " is-active" : ""}`} ref={mainRef}>
			<button className="projects-select__target drop-down__target" type="button" aria-label={label} onClick={() => setIsActive(!isActive)}>
				<span>
					<span>
						{label}
					</span>
				</span>
				<svg width="24" height="24">
					<use href="#drop-down-arrow"></use>
				</svg>
			</button>
			<div className="projects-select__block drop-down__block">
				<SimpleBar className="projects-select__block-inner">
					<ul className="projects-select__list">
						{(safeList && safeList.length) ? 
							(
								<li key="all-employees">
									<label className="popup-checkbox">
										<input onChange={(event) => handleCheckedAll(event.currentTarget.checked)}
											type="checkbox" name="all"
											className="popup-checkbox__input"
											ref={allCheckboxRef}
										/>
										<span className="popup-checkbox__element">
											<svg width="11" height="8" viewBox="0 0 11 8">
												<use xlinkHref="#check"></use>
											</svg>
										</span>
										<span className="popup-checkbox__text" style={{
											whiteSpace: "nowrap",
											textOverflow: "ellipsis",
											overflow: "hidden"
										}}>
											<b>{t("analytics_page.statistic_report.all_employees")}</b>
										</span>
									</label>
								</li>
							) : null
						}
						{
							(safeList && safeList.length) ? safeList
								.filter(employer => chosenItems?.some(item => item.id_num === employer.id_num))
								.filter(item => `${item.first_name} ${item.last_name}`.toLowerCase().includes(searchValue.toLowerCase()))
								?.map(employer =>
								<li key={`${employer.first_name}-${employer.id_num}`}>
									<label className="popup-checkbox">
										<input onChange={_ => handleSelectCategory(employer)}
											checked={chosenItems?.some(item => item.id_num === employer.id_num)}
											type="checkbox" name={employer.first_name + "-" + employer.last_name}
											className="popup-checkbox__input"
										/>
										<span className="popup-checkbox__element">
											<svg width="11" height="8" viewBox="0 0 11 8">
												<use xlinkHref="#check"></use>
											</svg>
										</span>
										<span className="popup-checkbox__text" style={{
											whiteSpace: "nowrap",
											textOverflow: "ellipsis",
											overflow: "hidden"
										}}>
											{employer.first_name} {employer.last_name}
										</span>
									</label>
								</li>
							) : <></>
						}
						{
							(safeList && safeList.length) ? safeList
								.filter(employer => !(chosenItems?.some(item => item.id_num === employer.id_num)))
								.filter(item => `${item.first_name} ${item.last_name}`.toLowerCase().includes(searchValue.toLowerCase()))
								?.map(employer =>
								<li key={`${employer.first_name}-${employer.id_num}`}>
									<label className="popup-checkbox">
										<input onChange={_ => handleSelectCategory(employer)}
											checked={chosenItems?.some(item => item.id_num === employer.id_num)}
											type="checkbox" name={employer.first_name + "-" + employer.last_name}
											className="popup-checkbox__input"
										/>
										<span className="popup-checkbox__element">
											<svg width="11" height="8" viewBox="0 0 11 8">
												<use xlinkHref="#check"></use>
											</svg>
										</span>
										<span className="popup-checkbox__text" style={{
											whiteSpace: "nowrap",
											textOverflow: "ellipsis",
											overflow: "hidden"
										}}>
											{employer.first_name} {employer.last_name}
										</span>
									</label>
								</li>
							) : <></>
						}
					</ul>
				</SimpleBar>
				<div className="projects-select__search">
					<label>
						<input type="search" name="search" required
							onChange={e => setSearchValue(e.target.value)} value={searchValue}
							className="input"
							placeholder={`${t("employees_admin.others.search_a_employee")}`}
						/>
					</label>
					<button className="btn is-grey" type="submit">
						<Translate>employees_page.table.search</Translate>
						<svg width="15" height="15" viewBox="0 0 15 15">
							<use xlinkHref="#search"></use>
						</svg>
					</button>
				</div>
			</div>
		</div>
	)
}